import isEmpty from 'lodash-es/isEmpty';
import { reverse } from 'named-urls';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DashboardBackButton, useDashboardFeatureFlags } from '@components';
import { Dashboard, PageLayout, UnavailableResource } from '@components/common';
import { useDashboardPermissions } from '@components/utils/DashboardPermissions';
import { Banner, Dropdown, HeadContent, Loading, Subsection, Timeline, ToolbarButton, useMediaQueryContext, } from '@fourthwall/components';
import { DotsHorizontalIcon } from '@fourthwall/icons';
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString';
import { routing } from '@utils/routing';
import * as S from './Order.styled';
import { Order as OrderLegacy } from './OrderLegacy';
import { CancelledOffers } from './components/CancelledOffers/CancelledOffers';
import { DonationItem } from './components/DonationItem/DonationItem';
import { OrderProfitSummary } from './components/OrderProfitSummary/OrderProfitSummary';
import { OrderSideSummary } from './components/OrderSideSummary/OrderSideSummary';
import { ShipmentItem } from './components/ShipmentItem/ShipmentItem';
import { useOrder } from './hooks/useOrder';
import { useOrderTimelineEvents } from './hooks/useOrderTimelineEvents';
import { useOrderCancellationQuery } from './queries/useOrderCancellationQuery';
import { useOrderRefundQuery } from './queries/useOrderRefundQuery';
import { getFullName } from './utils/getFullName';
export const Order = () => {
    const { dashboardFeatureFlags } = useDashboardFeatureFlags();
    const showNewOrder = dashboardFeatureFlags.orderV2.enabled;
    if (!showNewOrder) {
        return React.createElement(OrderLegacy, null);
    }
    return React.createElement(OrderNew, null);
};
const OrderNew = () => {
    const { orderId, order, banners, shippingMethodTooltip, isUnavailable, isLoading } = useOrder();
    const location = useLocation();
    const media = useMediaQueryContext();
    const navigate = useNavigate();
    const { hasPermission } = useDashboardPermissions();
    const { orderRefundQuery } = useOrderRefundQuery([orderId]);
    const { orderCancellationQuery } = useOrderCancellationQuery([orderId]);
    const timelineEnabled = hasPermission('contributions.orders.timelines');
    const { timelineEvents, isLoading: isLoadingTimelineEvents } = useOrderTimelineEvents(orderId);
    if (!order) {
        return React.createElement(Loading, { isLoading: isLoading });
    }
    const allShippedWithoutReshipments = order.shipments.filter((shipment) => shipment.status !== 'NOT_SHIPPED' && shipment.reshipment == false) || [];
    const getShipmentDeliveryIndex = (shipment) => allShippedWithoutReshipments.indexOf(shipment) + 1;
    const getToolbarContent = () => {
        const fullyCancellable = orderCancellationQuery.data?.fullyCancellable;
        const partiallyCancellable = orderCancellationQuery.data?.partiallyCancellable;
        const refundable = orderRefundQuery.data?.remainingRefundAmount &&
            orderRefundQuery.data.remainingRefundAmount.value > 0;
        const showEditOrderButton = (fullyCancellable || partiallyCancellable) && hasPermission('contributions.orders.edit');
        const showCancelOrderButton = (fullyCancellable || partiallyCancellable) && hasPermission('contributions.orders.cancel');
        const showRefundOrderButton = refundable && hasPermission('contributions.orders.refund');
        if (showEditOrderButton || showCancelOrderButton || showRefundOrderButton) {
            return (React.createElement(Dropdown, { content: React.createElement(React.Fragment, null,
                    showEditOrderButton && (React.createElement(Dropdown.Item, { label: "Edit order", onClick: () => {
                            navigate(reverse(routing.contributions.orders.order.edit, { orderId }));
                        } })),
                    showCancelOrderButton && (React.createElement(Dropdown.Item, { label: "Cancel items", onClick: () => {
                            navigate(reverse(routing.contributions.orders.order.cancel, { orderId }));
                        } })),
                    showRefundOrderButton && (React.createElement(Dropdown.Item, { label: "Refund order", onClick: () => {
                            navigate(reverse(routing.contributions.orders.order.refund, { orderId }));
                        } }))), position: "bottom-end" },
                React.createElement(ToolbarButton, { label: "Settings", icon: DotsHorizontalIcon, size: media.tablet ? 'medium' : 'small' })));
        }
        return null;
    };
    const renderReplacementNotification = () => {
        if (!order.replacement)
            return null;
        if (order.replacement.type === 'Replacement') {
            return (React.createElement(S.BannerWrapper, null,
                React.createElement(Banner, { appearance: "warning" },
                    "This is a replacement order.",
                    ' ',
                    React.createElement("a", { href: reverse(routing.contributions.orders.order.self, {
                            orderId: order.replacement?.sourceOrderId,
                        }), target: "_blank", rel: "noreferrer" }, "View original order"))));
        }
        return null;
    };
    const renderReplacementSourceNotification = () => {
        if (!order.replacement)
            return null;
        if (order.replacement.type === 'Source') {
            const hasManyReplacements = order.replacement?.replacementOrderIds?.length > 1;
            const title = hasManyReplacements
                ? 'This order was issued replacements.'
                : 'This order was issued a replacement.';
            return (React.createElement(S.BannerWrapper, null,
                React.createElement(Banner, { appearance: "warning" },
                    title,
                    ' ',
                    order.replacement?.replacementOrderIds?.map((replacementOrderId, index) => (React.createElement(React.Fragment, null,
                        React.createElement("a", { href: reverse(routing.contributions.orders.order.self, {
                                orderId: replacementOrderId,
                            }), target: "_blank", rel: "noreferrer" }, hasManyReplacements
                            ? `Replacement order ${index + 1}`
                            : 'View replacement order'),
                        ' '))))));
        }
        return null;
    };
    const renderLeftContent = () => (React.createElement(React.Fragment, null,
        renderReplacementNotification(),
        renderReplacementSourceNotification(),
        order && !order.profit && (React.createElement(S.BannerWrapper, null,
            React.createElement(Banner, { appearance: "danger" },
                "Part of data relating to profit is not loading.",
                ' ',
                React.createElement("a", { href: "mailto:hello@fourthwall.com" }, "Contact our support")))),
        banners &&
            banners.map((banner, index) => (React.createElement(S.BannerWrapper, null,
                React.createElement(Banner, { key: index, ...banner })))),
        React.createElement(Subsection, { title: "Order summary", borderless: true },
            order.shipments.map((shipment, index) => (React.createElement(ShipmentItem, { key: index, shipment: shipment, createdAt: order.createdAt, deliveryIndex: getShipmentDeliveryIndex(shipment), allDeliveriesCount: allShippedWithoutReshipments.length }))),
            order && order.offersCancelled.length > 0 && (React.createElement(CancelledOffers, { cancelledOffers: order.offersCancelled, cancellations: order.cancellations })),
            order.donation && React.createElement(DonationItem, { donation: order.donation }),
            order && (React.createElement(OrderProfitSummary, { order: order, shippingMethodTooltip: shippingMethodTooltip, totalRefundedAmount: orderRefundQuery.data?.totalRefundedAmount }))),
        order.message && React.createElement(Subsection, { title: "Message left" }, order.message),
        timelineEnabled && (React.createElement(Subsection, { title: "Timeline", borderless: true },
            React.createElement(Loading, { isLoading: isLoadingTimelineEvents }, isEmpty(timelineEvents) ? ('There are no events yet to display for this order.') : (React.createElement(Timeline, { events: timelineEvents, size: "large" })))))));
    const getSectionName = () => {
        const orderCopy = order.type === 'CREATOR_BUDGET' ? 'samples order' : 'order';
        const fullName = getFullName(order.shipping.address?.firstName, order.shipping.address?.lastName);
        const friendlyId = order.friendlyId;
        let sectionName = orderCopy;
        if (fullName) {
            const suffix = fullName.slice(-1).toLowerCase() === 's' ? `' ${orderCopy}` : `'s ${orderCopy}`;
            sectionName = `${fullName}${suffix}`;
        }
        if (friendlyId) {
            sectionName = `${sectionName} (#${friendlyId})`;
        }
        return sectionName.charAt(0).toUpperCase() + sectionName.slice(1);
    };
    if (isUnavailable) {
        return React.createElement(UnavailableResource, { resource: "order" });
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(HeadContent, { title: getSectionName() }),
        React.createElement(Loading, { isLoading: isLoading || orderRefundQuery.isLoading || orderCancellationQuery.isLoading },
            React.createElement(PageLayout, { leftContent: renderLeftContent(), rightContent: React.createElement(OrderSideSummary, { order: order }) },
                React.createElement("div", null,
                    React.createElement(Dashboard.ItemViewHeader, { name: getSectionName(), title: order.createdAt ? (React.createElement(TimezonedDateString, { date: order.createdAt, format: "[Placed] MMM D, YYYY [at] h:mm A" })) : null, backButton: React.createElement(DashboardBackButton, { label: "Orders", to: { pathname: routing.contributions.orders.all, search: location.search } }), toolbar: getToolbarContent() }))))));
};
